import { Injectable } from '@angular/core';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  DefaultUrlSerializer,
  NavigationBehaviorOptions,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationExtras,
  NavigationStart,
  Router,
  RouterEvent,
  RouterStateSnapshot,
  RoutesRecognized,
  UrlTree,
} from '@angular/router';
import { AuthUser } from '@app/core/models/auth-user';
import { BusinessRoutes, findRouteName } from '@app/routing/business-routes';
import { CurrentRouteData } from '@app/routing/models/current-route-data';
import { ResolvedRoute } from '@app/routing/models/resolved-route.interface';
import { RouteDataChange } from '@app/routing/models/route-data-change';
import { registerWithGlobalApp } from '@app/utils/app';
import { Identifier } from '@app/utils/common';
import { notEquals } from '@app/utils/equals';
import { PortalError } from '@app/utils/error/portal-error';
import { loggerFor } from '@app/utils/logger';
import { isNavigationEnd, isNavigationStart, isRoutesRecognized } from '@app/utils/route';
import { StringMap } from '@app/utils/string-map';
import { Observable, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, filter, first, map, startWith, tap } from 'rxjs/operators';
import { AuthenticationService } from '../auth/services/authentication.service';
import { UserLevel } from '../core/models/user-level';

@Injectable({
  providedIn: 'root',
})
export class RouterService {
  // to track first/initial navigation to set `old` route data after the first navigation happened
  private firstNavigationEndCompleted = false;

  private data: RouteDataChange = {
    old: null,
    current: null,
  };

  public get currentRouteData(): CurrentRouteData {
    return this.data.current;
  }

  private _currentRouteData$ = new ReplaySubject<CurrentRouteData>(1);

  get currentRouteData$(): Observable<CurrentRouteData> {
    return this._currentRouteData$.asObservable();
  }

  /**
   * Observable that contains the previous and current route data. Updates on NavigationEnd.
   */
  public get routeDataChange$(): Observable<RouteDataChange> {
    return this.routeChanged$.pipe(
      startWith(this.data),
      map(() => this.data),
      filter(() => notEquals(this.data.current, this.data.old))
    );
  }

  /**
   * Observable that contains the previous and current route data. Updates on RoutesRecognized.
   */
  public get routeDataChangeOnRecognized$(): Observable<RouteDataChange> {
    return this.routeRecognized$.pipe(
      startWith(this.data),
      map(() => this.data),
      filter(() => notEquals(this.data.current, this.data.old))
    );
  }

  public get routeChangeStart$(): Observable<NavigationStart> {
    return this.router.events.pipe(filter(isNavigationStart));
  }

  public get routeChanged$(): Observable<NavigationEnd> {
    return this.router.events.pipe(
      filter(isNavigationEnd),
      tap(() => {
        if (this.firstNavigationEndCompleted === false) {
          this.firstNavigationEndCompleted = true;
        }
      })
    );
  }

  public get routeRecognized$(): Observable<RoutesRecognized> {
    return this.router.events.pipe(filter(isRoutesRecognized));
  }

  constructor(
    private authenticationService: AuthenticationService,
    private router: Router
  ) {
    this.routeChangeStart$.subscribe((navigationStart) => {
      if (navigationStart.navigationTrigger === 'popstate') {
        const temp = this.router.routeReuseStrategy.shouldReuseRoute;

        this.router.routeReuseStrategy.shouldReuseRoute = () => false;

        this.router.events
          .pipe(
            filter(
              (navigation) =>
                navigation instanceof NavigationCancel ||
                navigation instanceof NavigationEnd ||
                navigation instanceof NavigationError
            ),
            first()
          )
          .subscribe(() => {
            this.router.routeReuseStrategy.shouldReuseRoute = temp;
          });
      }
    });

    // We need to update the route data as soon as the route has been recognized, so it is accurate
    // when used in the guard (guards are called before the navigation ends).
    this.router.events.pipe(filter(isRoutesRecognized)).subscribe((event: RoutesRecognized) => {
      const activatedRouteSnapshot = this.getActivatedRouteSnapshot(event.state);

      if (this.firstNavigationEndCompleted === true && this.data.current != null) {
        this.data.old = this.data.current;
      }

      this.updateCurrentRouteData(activatedRouteSnapshot, event.urlAfterRedirects);
    });

    registerWithGlobalApp('logRouteChanges', () => this.logRouteChanges());
  }

  /**
   * Check if the current route is either the specified route or a child of it.
   *
   * @param route Route to check
   */
  public currentRouteMatches(route: string) {
    let matches: boolean;

    const { name, url } = this.currentRouteData;

    if (name === route) {
      matches = true;
    } else {
      const resolvedUrl = this.resolveLink(route).url;

      matches = url.indexOf(resolvedUrl) === 0;
    }

    return matches;
  }

  /**
   * Parses the current url and returns information about it:
   *
   *  - Name of the current route
   *  - URL parameters (enterprise id, service provider id, user id)
   *  - Matrix parameters
   *
   * @param params
   */
  public extractParameters(params: any = {}): {
    matrixParameters: StringMap<string>;
    name: string | undefined;
    urlParameters: StringMap<string>;
  } {
    let route = this.router.routerState.root.snapshot;
    let routeName: string | null;
    let routeParams: StringMap<any> = {};

    let matrixParameters: StringMap<any> = {};

    // Iterate through children until we are at the last one (i.e. the current route)
    do {
      route = route.firstChild;

      routeParams = {
        ...routeParams,
        ...route.params,
      };
    } while (route.firstChild);

    routeName = route.data.name ?? null;

    // The route may not be up-to-date (e.g. on features or devices page when toggling accordions/tabs)
    // so we parse the actual url and traverse it to get the current matrix parameters
    let path = new DefaultUrlSerializer().parse(window.location.pathname).root;

    // Get the matrix parameters of the last url segment
    if (path.children && path.children.primary && path.children.primary.segments) {
      const segments = path.children.primary.segments;

      if (segments.length > 0) {
        matrixParameters = segments[segments.length - 1].parameters;
      }

      if (routeName == null) {
        const fullPath = '/' + segments.map((segment) => segment.path).join('/');

        routeName = findRouteName(fullPath);
      }
    }

    // Combine route parameters (matrix and url parameters at this point) with user defined parameters
    const urlParameters = {
      ...routeParams,
      ...params,
    };

    // Remove matrix parameters from the url parameters
    Object.keys(matrixParameters).forEach((key) => {
      delete urlParameters[key];
    });

    return {
      matrixParameters,
      name: routeName,
      urlParameters,
    };
  }

  /**
   * Replacement for UserLevelToRouteMapping object, improved check for startpage
   */
  private buildInitialRoute(authUser: AuthUser, userLevel: UserLevel): string {
    switch (userLevel) {
      case UserLevel.EndUser:
        return 'end-user-personalization';

      case UserLevel.EnterpriseAdmin:
        if (
          authUser.role.permissions.includes('MANAGE_USER') ||
          authUser.role.permissions.includes('VIEW_USER')
        ) {
          return 'end-user-overview';
        } else {
          return 'enterprise-settings-customer';
        }

      case UserLevel.ServiceProviderAdmin:
      case UserLevel.SystemAdmin:
        return 'service-provider-enterprises';
      default:
        return '';
    }
  }

  /**
   * Resolve the initial route for the given user level
   * @param userLevel
   * @param params Additional menu parameters to override the current context
   */
  public initialRoute(
    userLevel: UserLevel = this.authenticationService.authUserLevel,
    params?: StringMap<Identifier>
  ): string {
    return this.resolveLink(this.buildInitialRoute(this.authenticationService.authUser, userLevel), params)
      .url;
  }

  public logRouteChanges(): void {
    const logger = loggerFor('Route changed');

    const isRouterEvent = (o: any): o is RouterEvent => o instanceof RouterEvent;

    this.router.events
      .pipe(
        filter(isRouterEvent),
        distinctUntilChanged((a, b) => a.id === b.id && Object.getPrototypeOf(a) === Object.getPrototypeOf(b))
      )
      .subscribe((value) => {
        logger.log(value);
      });
  }

  /**
   * Wrapper function for the Angular router.navigateByUrl (so we don't need to inject the router as well as the router service)
   * @param url
   * @param extras
   */
  public navigateByUrl(url: string | UrlTree, extras?: NavigationExtras): Promise<boolean> {
    return this.router.navigateByUrl(url, extras);
  }

  public navigateToRoute(name: string, params?: StringMap<any>, extras?: NavigationExtras): Promise<Boolean> {
    const route = this.resolveLink(name, params);

    return this.navigateByUrl(route.url, extras);
  }

  /**
   * Resolves the initial url for the given user level and navigates to it
   * @param userLevel
   * @param params Additional menu parameters to override the current context
   */
  public navigateToInitialRoute(userLevel?: UserLevel, params?: StringMap<Identifier>): Promise<boolean> {
    return this.router.navigateByUrl(this.initialRoute(userLevel, params), {
      replaceUrl: true,
    });
  }

  resolveLink$(name: string, params: any = {}): Observable<ResolvedRoute> {
    return this.routeDataChange$.pipe(map(() => this.resolveLink(name, params)));
  }

  /**
   * Resolves a link by the given name and replaces given parameters.
   * Returns the resolved url and a map of parameters that weren't replaced in the url
   * @param name Name of the route
   * @param params Optional map of parameters
   * @param addRemainingAsMatrixParams If true, add any parameters not present in the route as matrix parameters at the end of the resolved URL.
   */
  public resolveLink(
    name: string,
    params: any = {},
    addRemainingAsMatrixParams: boolean = false
  ): ResolvedRoute {
    let route: ResolvedRoute;

    const namedRoutes: StringMap<string> = BusinessRoutes;

    if (namedRoutes[name]) {
      route = this.replaceUrlParameters(namedRoutes[name], params);
    } else {
      throw {
        cause: new Error(`Cannot resolve route with name ${name}`),
        id: 'resolve-route',
      } as PortalError;
    }

    if (addRemainingAsMatrixParams) {
      const urlTree = this.router.createUrlTree([route.url, route.nonUrlParams]);

      route.url = urlTree.toString();
    }

    return route;
  }

  /**
   * Takes an url and a map of parameters and replaces all parameters that are defined in the url.
   * Returns the processed url and a map of parameters that weren't replaced in the url
   * @param raw url to process
   * @param params Map of parameters
   */
  public replaceUrlParameters(raw: string, params: StringMap<any>): ResolvedRoute {
    // Map of parameters that weren't defined in the url.
    // We collect and return them so the user can use them either as query or matrix parameters.
    let nonUrlParams: StringMap<any> = {};

    Object.keys(params).forEach((key) => {
      const term = `:${key}`;

      if (raw.indexOf(term) > -1) {
        raw = raw.replace(term, params[key]);
      } else {
        nonUrlParams[key] = params[key];
      }
    });

    const authUser = this.authenticationService.authUser;
    const routeData = this.currentRouteData;

    const data = {
      enterpriseId: routeData.params?.enterpriseId ?? authUser?.enterpriseName,
      serviceProviderId: routeData.params?.serviceProviderId ?? authUser?.serviceProviderName,
      userId: routeData.params?.userId ?? authUser?.username,
      userLevel: routeData.params?.userLevel ?? authUser?.role.level,
    };

    let url = raw
      .replace(':serviceProviderId', data.serviceProviderId)
      .replace(':enterpriseId', data.enterpriseId)
      .replace(':userId', data.userId);

    if (routeData?.params != null) {
      // Replace all params that are already present in the url but weren't replace by now
      Object.entries(routeData.params).forEach(([key, value]) => {
        url = url.replace(`:${key}`, value);
      });
    }

    return {
      nonUrlParams,
      url,
    };
  }

  /**
   * Refreshes the current route and overrides existing parameters with the given ones.
   *
   * @param params Parameters to replace existing url parameters
   * @param parameterName Name of the parameter that was changed
   */
  public refreshRoute(params: any = {}, parameterName: string): Promise<boolean> {
    // Extract url and matrix parameters
    const { name, matrixParameters } = this.extractParameters(params);

    // Find the route based on the current route name
    const namedRoutes: StringMap<string> = BusinessRoutes;

    // Check if a route parameter has changed that is vital to how routes are built (which is
    // possibly any route parameter that is contained by the menu data object). If such a parameter
    // is changed then let's update the menu directly (which would otherwise occur after the menu
    // state is synced to the store). This is necessary because it could happen (and it does in the
    // case of the sub-nav component) that a component wants to create a route inside a route change
    // event. If we didn't update the menu data, the created route would use the wrong parameters
    // and therefore would not be correct. In the case of the sub-nav component this is a problem
    // because it checks if the current route matches a pre-defined route and if it does, the
    // sub-nav will be shown. But if the menu data is not up-to-date then the pre-defined route
    // would not be matched and therefore the sub-nav would not be visible.
    // if (this.menu[parameterName] != null) {
    //   const updatedMenu = {
    //     ...this.menu,
    //     [parameterName]: params[parameterName],
    //   };
    //
    //   if (parameterName === 'enterpriseId') {
    //     updatedMenu.enterpriseName = params['enterpriseName'];
    //   }
    //
    //   this.menu = updatedMenu;
    // }

    let path = namedRoutes[name];

    // Check if the parameterName that has changed is the last parameter. If not then remove
    // everything starting with the next parameter.
    const regex = new RegExp(`^(.*\/)?(?:(:${parameterName}$)|(:${parameterName}\/.*?)\/:.*$)`);
    const match = regex.exec(path);

    if (match != null) {
      path = match.slice(1).join('');
    }

    // Replace the url parameters
    const route = this.replaceUrlParameters(path, params);

    // And add all matrix parameters that were defined
    const url = this.router.createUrlTree([route.url, matrixParameters]);

    // Save the current routeReuseStrategy.shouldReuseRoute
    const temp = this.router.routeReuseStrategy.shouldReuseRoute;

    // Set it to "don't reuse routes"
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;

    // Reset after angular stopped the navigation
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd || event instanceof NavigationCancel))
      .subscribe(() => {
        this.router.routeReuseStrategy.shouldReuseRoute = temp;
      });

    // Navigate to the newly created url
    return this.navigateByUrl(url);
  }

  /**
   * Get matrix params from the current `activatedRoute`.
   */
  public getMatrixParams(activatedRoute: ActivatedRoute): { [name: string]: string } {
    /*
     * NOTE: We need to parse the URI and take the matrix params from last segment.
     * The activatedRoute does not have it when we use `updateMatrixParams` because we use `location.replaceState
     */
    // let matrixParameters = {};

    // // The route may not be up-to-date (e.g. on features or devices page when toggling accordions/tabs)
    // // so we parse the actual url and traverse it to get the current matrix parameters
    // let path = new DefaultUrlSerializer().parse(window.location.pathname).root;

    // // Get the matrix parameters of the last url segment
    // if (path.children && path.children.primary && path.children.primary.segments) {
    //   const segments = path.children.primary.segments;

    //   if (segments.length > 0) {
    //     matrixParameters = segments[segments.length - 1].parameters;
    //   }
    // }

    // return matrixParameters

    return activatedRoute.snapshot.url[activatedRoute.snapshot.url.length - 1].parameters;
  }

  /**
   * Set matrix params on the last part of the URL by navigating to the URL of the activated route
   * @param activatedRoute current activated route
   * @param params matrix parameters to be set on the current activated route
   * @param extras An object containing properties that modify the navigation strategy.
   * @returns A Promise that resolves to 'true' when navigation succeeds,
   * to 'false' when navigation fails, or is rejected on error.
   *
   * Note: if you want to influence how location change is placed in history then provide `extras` where
   * you can `skipLocationChange` and/or `replaceUrl` in browser navigation history.
   */
  public setMatrixParams(
    activatedRoute: ActivatedRoute,
    params: { [name: string]: string } = {},
    extras?: NavigationBehaviorOptions
  ): Promise<boolean> {
    const url = this.router.createUrlTree([params], {
      relativeTo: activatedRoute,
    });

    return this.router.navigateByUrl(url, extras);
  }

  private getActivatedRouteSnapshot(state: RouterStateSnapshot): ActivatedRouteSnapshot {
    let route = state.root;

    do {
      route = route.firstChild;
    } while (route?.firstChild);

    return route;
  }

  private updateCurrentRouteData(currentRoute: ActivatedRouteSnapshot, url: string): void {
    this.data.current = this.getRouteData(currentRoute, url);

    this._currentRouteData$.next(this.data.current);
  }

  private getRouteData(currentRoute: ActivatedRouteSnapshot, url: string): CurrentRouteData {
    return {
      context: {
        enterpriseId: currentRoute?.params?.enterpriseId,
        serviceProviderId: currentRoute?.params?.serviceProviderId,
        userId: currentRoute?.params?.userId,
      },
      data: currentRoute?.data ?? {},
      name: currentRoute?.data?.name ?? findRouteName(url),
      params: currentRoute?.params,
      url: decodeURIComponent(url),
    };
  }
}
